<template>
  <div class="view-container">
    <!-- 美化后的搜索栏 -->
    <div class="search-panel-v2">
      <div class="search-bar">
        <div class="search-input-wrap">
          <i class="fas fa-search search-icon"></i>
          <input v-model="searchKeyword" type="text" class="search-input" placeholder="搜索报表名称或描述..." @keyup.enter="doSearch">
          <button v-if="searchKeyword" class="search-clear" @click="searchKeyword=''"><i class="fas fa-times"></i></button>
        </div>
        <div class="search-input-wrap" style="max-width:200px">
          <i class="fas fa-user search-icon"></i>
          <input v-model="creatorFilter" type="text" class="search-input" placeholder="创建人筛选..." @keyup.enter="doSearch">
          <button v-if="creatorFilter" class="search-clear" @click="creatorFilter=''"><i class="fas fa-times"></i></button>
        </div>
        <span class="search-info">共 {{ filteredReports.length }} 个自定义报表</span>
        <button v-if="searchKeyword || creatorFilter" @click="resetSearch" class="btn btn-reset"><i class="fas fa-undo"></i> 清除</button>
      </div>
    </div>

    <div class="category-tabs">
      <button v-for="cat in categories" :key="cat.id" class="category-tab" :class="{ active: activeCategory === cat.id }" @click="activeCategory = cat.id">
        <i :class="cat.icon"></i><span>{{ cat.name }}</span>
      </button>
      <span style="margin-left:auto;font-size:12px;color:#999">共 {{ reports.length }} 条，显示 {{ filteredReports.length }} 条</span>
    </div>

    <div class="report-grid" v-if="!loadingReports">
      <div v-for="(report, idx) in filteredReports" :key="report.guid || idx" class="report-card-wrapper">
        <div class="report-card" @click="openReport(report)">
          <div class="report-card-header">
            <div class="report-icon" :style="{ background: bgColors[idx % bgColors.length] }"><i class="fas fa-chart-line"></i></div>
            <div class="report-title-area">
              <h3 class="report-name">{{ report.chName || report.ChName || '-' }}</h3>
              <p class="report-desc">{{ report.description || report.Description || '暂无描述' }}</p>
            </div>
            <div class="report-arrow"><i class="fas fa-arrow-right"></i></div>
          </div>
          <div class="report-meta-row">
            <span class="meta-tag"><i class="fas fa-user"></i> {{ report.creater || report.Creater || '-' }}</span>
            <span class="meta-tag"><i class="fas fa-calendar"></i> {{ formatDate(report.createTime || report.CreateTime) }}</span>
            <span class="meta-tag"><i class="fas fa-tag"></i> {{ getChartTypeName(report.enName || report.EnName) }}</span>
          </div>
          <div class="report-preview">
            <VChart v-if="report._previewOption" class="preview-chart" :option="report._previewOption" autoresize />
            <div v-else class="preview-loading"><i class="fas fa-spinner fa-pulse"></i><span>加载中...</span></div>
          </div>
        </div>
      </div>
      <div v-if="filteredReports.length === 0" class="empty-grid"><i class="fas fa-chart-line"></i><span>暂无自定义报表</span></div>
    </div>
    <div v-else class="loading-container"><i class="fas fa-spinner fa-pulse fa-2x"></i><p>正在加载报表...</p></div>

  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue'
import { useRouter } from 'vue-router'
import { use } from 'echarts/core'
import { CanvasRenderer } from 'echarts/renderers'
import { PieChart, BarChart, LineChart } from 'echarts/charts'
import { TitleComponent, TooltipComponent, LegendComponent, GridComponent } from 'echarts/components'
import VChart from 'vue-echarts'
import { getAllReports, executeReportSql, getCatalogs } from '../api/report'

use([CanvasRenderer, PieChart, BarChart, LineChart, TitleComponent, TooltipComponent, LegendComponent, GridComponent])

// ==================== 状态 ====================
const router = useRouter()
const activeCategory = ref('all')
const searchKeyword = ref('')
const creatorFilter = ref('')
const loadingReports = ref(false)
const reports = ref([])

const bgColors = ['#6366f1', '#10b981', '#f59e0b', '#ec4899', '#ef4444', '#8b5cf6', '#3B82F6', '#14B8A6']

// catalogGuid -> { id: catalogGuid, name: EnName } 映射
const catalogMap = ref({})
const loadedCategories = ref([])

const categories = computed(() => [
  { id: 'all', name: '全部', icon: 'fas fa-layer-group' },
  ...loadedCategories.value
])

/** 大小写不敏感获取字段值 */
const getField = (obj, key) => {
  if (!obj || !key) return undefined
  if (obj[key] !== undefined) return obj[key]
  const lower = key.toLowerCase()
  for (const k of Object.keys(obj)) {
    if (k.toLowerCase() === lower) return obj[k]
  }
  return undefined
}

/** 格式化日期 */
const formatDate = (val) => {
  if (!val) return '-'
  const str = String(val)
  if (str.length >= 10) return str.slice(0, 10)
  return str
}

/** 图表类型名称 */
const getChartTypeName = (enName) => {
  const map = { '0': '列表', '1': '饼图', '2': '柱状图', '3': '折线图', '4': '饼图+列表', '5': '柱状图+列表', '6': '折线图+列表' }
  return map[String(enName)] || '未知'
}

/**
 * 根据图表类型和 SQL 查询数据构建 ECharts option
 * @param {string} enName - 报表图表类型编码
 * @param {Array} data - SQL 返回的行数据
 * @param {string} xField - X轴字段名
 * @param {string} yField - Y轴字段名
 */
const buildChartOption = (enName, data, xField, yField) => {
  if (!data || data.length === 0) return null

  const typeNum = String(enName)
  const xValues = data.map(row => String(getField(row, xField) ?? ''))
  const yValues = data.map(row => {
    const v = getField(row, yField)
    const n = Number(v)
    return isNaN(n) ? v : n
  })

  // 饼图: 1, 4
  if (typeNum === '1' || typeNum === '4') {
    const pieData = data.map((row, i) => ({
      name: xValues[i] || `项目${i + 1}`,
      value: typeof yValues[i] === 'number' ? yValues[i] : 0
    }))
    return {
      tooltip: { trigger: 'item', formatter: '{b}: {c} ({d}%)' },
      legend: { type: 'scroll', orient: 'vertical', right: 10, top: 20, bottom: 20, textStyle: { fontSize: 11 } },
      series: [{
        type: 'pie',
        radius: ['40%', '68%'],
        center: ['45%', '50%'],
        data: pieData,
        label: { fontSize: 11, formatter: '{b}: {d}%' },
        emphasis: { itemStyle: { shadowBlur: 10, shadowOffsetX: 0, shadowColor: 'rgba(0,0,0,0.3)' } }
      }]
    }
  }

  // 柱状图: 2, 5
  if (typeNum === '2' || typeNum === '5') {
    return {
      tooltip: { trigger: 'axis' },
      grid: { left: 50, right: 20, top: 20, bottom: 30 },
      xAxis: { type: 'category', data: xValues, axisLabel: { fontSize: 10, rotate: xValues.length > 8 ? 30 : 0 } },
      yAxis: { type: 'value', axisLabel: { fontSize: 10 } },
      series: [{
        type: 'bar',
        data: yValues,
        itemStyle: { color: '#6366f1', borderRadius: [4, 4, 0, 0] }
      }]
    }
  }

  // 折线图: 3, 6
  if (typeNum === '3' || typeNum === '6') {
    return {
      tooltip: { trigger: 'axis' },
      grid: { left: 50, right: 20, top: 20, bottom: 30 },
      xAxis: { type: 'category', data: xValues, axisLabel: { fontSize: 10, rotate: xValues.length > 8 ? 30 : 0 } },
      yAxis: { type: 'value', axisLabel: { fontSize: 10 } },
      series: [{
        type: 'line',
        data: yValues,
        smooth: true,
        itemStyle: { color: '#10b981' },
        areaStyle: { color: 'rgba(16,185,129,0.1)' }
      }]
    }
  }

  return null
}

// ==================== 计算属性 ====================

const filteredReports = computed(() => {
  return (reports.value || []).filter((r) => {
    const name = getField(r, 'chName') || ''
    const desc = getField(r, 'description') || ''
    const creater = getField(r, 'creater') || ''
    const category = r._category

    if (activeCategory.value !== 'all' && (category?.id || '') !== activeCategory.value) return false
    if (searchKeyword.value) {
      const kw = searchKeyword.value.toLowerCase()
      if (!String(name).toLowerCase().includes(kw) && !String(desc).toLowerCase().includes(kw)) return false
    }
    if (creatorFilter.value && !String(creater).includes(creatorFilter.value)) return false
    return true
  })
})

// ==================== 方法 ====================

/** 根据报表的 CatalogGuid 匹配实际目录 */
const getReportCategory = (catalogGuid) => {
  if (!catalogGuid) return null
  return catalogMap.value[catalogGuid] || null
}

/** 加载报表目录树并构建映射（排除效率报表目录） */
const loadCatalogs = async () => {
  try {
    const res = await getCatalogs()
    const list = Array.isArray(res?.data) ? res.data : (Array.isArray(res?.Data) ? res.Data : [])
    const map = {}
    const categoriesList = []
    list.forEach(c => {
      const guid = c.guid || c.Guid || ''
      const name = c.enName || c.EnName || ''
      if (guid && !map[guid]) {
        map[guid] = { id: guid, name, icon: 'fas fa-folder-open' }
        // 效率报表目录不显示在分类Tab中，但保留在map中用于过滤
        if (name !== '效率报表') {
          categoriesList.push({ id: guid, name, icon: 'fas fa-folder-open' })
        }
      }
    })
    catalogMap.value = map
    loadedCategories.value = categoriesList
  } catch (e) { console.error('加载报表目录失败', e) }
}

/** 加载所有自定义报表（排除效率报表）并执行 SQL 获取预览数据 */
const loadReports = async () => {
  loadingReports.value = true
  try {
    const res = await getAllReports()
    // 兼容多种响应格式
    let list = Array.isArray(res) ? res : (res?.data || res?.Data || res?.records || [])
    // 找到"效率报表"目录的 Guid，排除该目录下的报表
    let efficientGuid = ''
    for (const [guid, cat] of Object.entries(catalogMap.value)) {
      if (cat.name === '效率报表') { efficientGuid = guid; break }
    }
    if (efficientGuid) {
      list = list.filter(r => {
        const cg = getField(r, 'catalogGuid') || ''
        return cg !== efficientGuid
      })
    }
    console.log('CustomReports loaded:', list.length, 'reports (excluded 效率报表 guid=', efficientGuid, ')')
    if (!list || list.length === 0) {
      reports.value = []
      loadingReports.value = false
      return
    }

    // 为每个报表分配分类和预览数据
    const enriched = list.map(r => ({
      ...r,
      _category: getReportCategory(getField(r, 'catalogGuid') || getField(r, 'CatalogGuid'))
    }))

    reports.value = enriched

    // 异步加载每个报表的预览数据
    for (const r of enriched) {
      const sql = getField(r, 'para5') || getField(r, 'Para5')
      const para0 = getField(r, 'para0') || getField(r, 'Para0')
      const para2 = getField(r, 'para2') || getField(r, 'Para2')
      const para3 = getField(r, 'para3') || getField(r, 'Para3')
      const enName = getField(r, 'enName') || getField(r, 'EnName')
      if (sql) {
        try {
          const sqlRes = await executeReportSql(sql, String(para0) === '1')
          const rows = Array.isArray(sqlRes) ? sqlRes : (sqlRes?.data || sqlRes?.records || [])
          if (rows && rows.length > 0) {
            r._previewOption = buildChartOption(enName, rows, para2, para3)
            r._previewData = rows
          }
        } catch (e) {
          // 预览 SQL 执行失败，忽略
          r._previewError = true
        }
      }
    }
    // 触发响应式更新
    reports.value = [...enriched]
  } catch (e) {
    console.error('加载自定义报表失败:', e)
    reports.value = []
  } finally {
    loadingReports.value = false
  }
}

/** 打开报表 - 新窗口 */
const openReport = (report) => {
  const guid = getField(report, 'guid') || getField(report, 'Guid') || ''
  if (!guid) return
  const resolved = router.resolve({ path: '/report-viewer', query: { reportGuid: guid } })
  window.open(resolved.href, '_blank')
}

const doSearch = () => {}
const resetSearch = () => { searchKeyword.value = ''; creatorFilter.value = '' }

// ==================== 生命周期 ====================
onMounted(async () => {
  await loadCatalogs()
  loadReports()
})
</script>

<style scoped>
.view-container { padding: 20px; background: #fff; border-radius: 8px; box-shadow: 0 2px 8px rgba(0,0,0,0.05); display: flex; flex-direction: column; max-height: calc(100vh - 150px); overflow: hidden; margin: 16px; }

/* ============ 美化搜索栏 ============ */
.search-panel-v2 { flex-shrink: 0; margin-bottom: 16px; }
.search-bar { display: flex; align-items: center; gap: 12px; padding: 10px 16px; background: #f8fafc; border: 1px solid #e2e8f0; border-radius: 12px; flex-wrap: wrap; }
.search-input-wrap { position: relative; flex: 1; min-width: 200px; max-width: 360px; }
.search-icon { position: absolute; left: 12px; top: 50%; transform: translateY(-50%); color: #94a3b8; font-size: 14px; pointer-events: none; z-index: 1; }
.search-input { width: 100%; padding: 9px 36px 9px 36px; border: 1px solid #e2e8f0; border-radius: 8px; font-size: 13px; color: #334155; background: #fff; outline: none; transition: all 0.2s; box-sizing: border-box; }
.search-input:focus { border-color: #6366f1; box-shadow: 0 0 0 3px rgba(99,102,241,0.1); }
.search-clear { position: absolute; right: 6px; top: 50%; transform: translateY(-50%); background: none; border: none; color: #94a3b8; cursor: pointer; padding: 4px 6px; font-size: 13px; border-radius: 50%; transition: all 0.15s; }
.search-clear:hover { color: #ef4444; background: #fee2e2; }
.search-info { font-size: 12px; color: #94a3b8; white-space: nowrap; margin-left: 4px; }
.btn-reset { display: inline-flex; align-items: center; gap: 4px; padding: 8px 14px; background: #fff; border: 1px solid #e2e8f0; border-radius: 8px; font-size: 12px; color: #64748b; cursor: pointer; transition: all 0.2s; }
.btn-reset:hover { background: #f1f5f9; color: #6366f1; border-color: #6366f1; }

.category-tabs { display: flex; gap: 10px; margin-bottom: 20px; flex-wrap: wrap; flex-shrink: 0; }
.category-tab { display: flex; align-items: center; gap: 6px; padding: 7px 18px; background: #f3f4f6; border: 1px solid #e7eaec; border-radius: 20px; font-size: 13px; color: #676a6c; cursor: pointer; transition: all 0.2s ease; }
.category-tab:hover { border-color: #10b981; color: #10b981; }
.category-tab.active { background: #10b981; border-color: #10b981; color: #fff; }
.report-grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(420px, 1fr)); gap: 20px; overflow-y: auto; flex: 1; padding: 2px; }
.report-card-wrapper { min-width: 0; }
.report-card { background: #fff; border: 1px solid #e7eaec; border-radius: 10px; overflow: hidden; cursor: pointer; transition: all 0.25s ease; }
.report-card:hover { border-color: #10b981; box-shadow: 0 4px 20px rgba(16,185,129,0.12); transform: translateY(-2px); }
.report-card-header { display: flex; align-items: center; gap: 14px; padding: 16px 18px 8px; }
.report-icon { width: 50px; height: 50px; border-radius: 12px; display: flex; align-items: center; justify-content: center; color: #fff; font-size: 22px; flex-shrink: 0; }
.report-title-area { flex: 1; min-width: 0; }
.report-name { font-size: 15px; font-weight: 600; color: #333; margin: 0 0 4px 0; }
.report-desc { font-size: 12px; color: #888; margin: 0; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.report-arrow { color: #ccc; font-size: 16px; transition: all 0.2s; }
.report-card:hover .report-arrow { color: #10b981; }
.report-meta-row { display: flex; gap: 16px; padding: 0 18px 8px; }
.meta-tag { font-size: 11px; color: #999; display: flex; align-items: center; gap: 4px; }
.preview-chart { height: 170px; }
.preview-loading { height: 170px; display: flex; align-items: center; justify-content: center; gap: 8px; color: #999; font-size: 13px; }
.empty-grid { grid-column: 1/-1; text-align: center; padding: 60px; color: #999; }
.empty-grid i { font-size: 48px; margin-bottom: 10px; display: block; }
.loading-container { display: flex; flex-direction: column; align-items: center; justify-content: center; padding: 80px; color: #999; gap: 12px; }
</style>
